<template>
  <div class="agila-radio-buttons">
    <fieldset
      class="radio-button-wrapper"
      :class="props.classes"
      role="radiogroup"
      :aria-label="props.label ? props.label : props.ariaLabel"
    >
      <label :id="props.id + '-label'" :for="props.id" v-if="props.label">{{
        props.label
      }}</label>
      <agila-form-error
        :error="error"
        :id="props.id + '-error'"
      ></agila-form-error>
      <div class="radio-button-cons" :class="{ icons: props.icon }">
        <div
          class="radio-button-con"
          :class="{ active: inputValue && option.id === inputValue.id }"
          v-for="option of props.options"
          :key="option.id"
          @click="
            inputValue = option;
            readyForCheck = true;
            updateValue();
          "
        >
          <RadioButton
            v-model="inputValue"
            :inputId="props.id + '_' + option.id"
            :name="props.id"
            :value="option"
            :tabindex="0"
            :invalid="!!error"
            :aria-describedby="props.id + '-error'"
            :aria-labelledby="'label_' + props.id + '_' + option.id.toString()"
          />
          <label
            :for="props.id + '_' + option.id.toString()"
            :id="'label_' + props.id + '_' + option.id.toString()"
            >{{ option.name }}</label
          >
          <div
            v-if="props.icon"
            :class="{
              inactive: inputValue.id !== option.id,
              ['agila-icon-' + option[props.icon]]: true,
            }"
          ></div>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { defineEmits, defineProps, withDefaults, defineExpose } from "vue";
import RadioButton from "primevue/radiobutton";
import useSharedFormLogic from "../helper/functions/useSharedFormLogic";
import AgilaFormError from "./agilaFormError.vue";

interface Props {
  modelValue: any;
  id: string;
  options: Array<any>;
  label?: string;
  rules?: string[];
  classes?: string;
  icon?: string;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  id: "",
  options: () => [],
  label: "",
  rules: () => [],
  classes: "",
  icon: "",
  ariaLabel: "",
});

const emit = defineEmits<{
  (e: "update:modelValue", modelValue: any): void;
  (e: "update:error", error: string): void;
}>();

const { readyForCheck, inputValue, error, updateValue, validate } =
  useSharedFormLogic(props, emit);

defineExpose({
  validate: () => {
    readyForCheck.value = true;
    return validate(inputValue.value);
  },
});
</script>

<style lang="scss">
.agila-radio-buttons {
  position: relative;
  margin-bottom: 20px;

  .radio-button-wrapper {
    > label {
      margin-bottom: 8px !important;
      display: block;
    }
    label {
      font-family: var(--font-open-sans-regular), sans-serif;
      color: var(--color-gray-700);
    }
    .radio-button-cons {
      margin-top: 8px;

      .radio-button-con {
        margin-top: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;

        .p-radiobutton {
          margin-right: 8px;

          .p-radiobutton-box {
            box-sizing: border-box;
          }

          &:hover .p-radiobutton-box {
            border: 2px solid var(--color-blue-600);
          }
          &.p-invalid {
            .p-radiobutton-box {
              border: 2px solid var(--color-red-700);
            }
          }
        }

        label {
          cursor: pointer;
          margin-bottom: 0;
          padding-bottom: 0;
        }
      }
      div:nth-of-type(1) {
        margin-top: 0;
      }

      &.icons {
        display: flex;
        align-items: baseline;

        .radio-button-con {
          align-items: baseline;
          margin-left: 32px;
          margin-top: 0;
          display: block;

          .p-radiobutton {
            margin-right: 0;
          }
        }

        div:nth-of-type(1) {
          margin-left: 0;
        }
      }
    }
  }

  .p-radiobutton {
    &:focus {
      .p-radiobutton-box {
        box-shadow: 0 0 0 1px var(--color-white),
          0 0 0 3px var(--color-blue-600);
        background: none;
      }
    }
    .p-radiobutton-box {
      &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-yellow-300),
          0 0 0 4px var(--color-blue-focus-line) !important;
        color: var(--color-gray-900);
        border-color: var(--color-yellow-300);
        outline: none;
      }
      &:hover {
        border-color: var(--color-yellow-300);
        color: var(--color-gray-900);
      }
    }
  }
}
</style>
